import { useState } from "react";
import type { DisplayGroup, DisplayVariant } from "../../stores/targetANDtacticsStore";

type DisplayGroupAccordionProps = {
    group: DisplayGroup;
    onVariantInfoClick: (reasoning: string) => void;
};

type TargetCard = {
    label: string;
    subLabel?: string;
    value?: string | null;
    supplierBid?: string | null;
    bullets?: string[];
    isBatna?: boolean;
    reasoning?: string | null;
};

const formatCurrency = (val: number | null | undefined, currency?: string | null) => {
    if (val === null || val === undefined) return null;
    const currencyCode = currency || 'EUR';
    return `${currencyCode} ${val.toFixed(4)}`;
};

const formatNumber = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '-';
    return val.toLocaleString();
};

const buildTargetCardsFromVariant = (variant: DisplayVariant): TargetCard[] => {
    const cards: TargetCard[] = [];
    const currency = variant.bidCurrency || variant.target?.currency;
    const supplierBidFormatted = formatCurrency(variant.bidPrice, currency);

    cards.push({
        label: "MDO",
        subLabel: "(Most Desired Outcome)",
        value: formatCurrency(variant.target?.mdo, currency) ?? "-",
        supplierBid: supplierBidFormatted,
    });

    cards.push({
        label: "Supplier Target",
        value: formatCurrency(variant.target?.supplierTarget, currency) ?? "-",
        reasoning: variant.target?.reasoning?.llmReasoning,
    });

    cards.push({
        label: "LAA",
        subLabel: "(Least Acceptable Agreement)",
        value: formatCurrency(variant.target?.laa, currency) ?? "-",
        supplierBid: supplierBidFormatted,
    });

    // BATNA
    cards.push({
        label: "BATNA",
        subLabel: "(Best Alternative to a Negotiated Agreement)",
        value: variant.target?.batna || "-",
        isBatna: true,
    });

    return cards;
};

type VariantAccordionProps = {
    variant: DisplayVariant;
    onInfoClick: () => void;
    onReasoningClick: (reasoning: string) => void;
};

const VariantAccordion = ({ variant, onInfoClick, onReasoningClick }: VariantAccordionProps) => {
    const [open, setOpen] = useState(false);
    const targetCards = buildTargetCardsFromVariant(variant);

    return (
        <div className={`briefing-doc__target-accordion ${open ? "is-open" : ""}`}>
            <button
                type="button"
                className="briefing-doc__target-toggle"
                onClick={() => setOpen(!open)}
            >
                <span>{variant.productDescription}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {variant.target?.reasoning?.targetPriceReasoning && (
                        <i
                            className="pi pi-eye"
                            style={{
                                color: 'var(--text-muted)',
                                padding: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onInfoClick();
                            }}
                            title="View target price reasoning"
                        />
                    )}
                    <i className={`pi ${open ? "pi-chevron-up" : "pi-chevron-down"}`} />
                </span>
            </button>
            {open && (
                <div className="briefing-doc__target-cards">
                    {targetCards.length === 0 && (
                        <div className="briefing-doc__muted">No target data available.</div>
                    )}
                    {targetCards.map((c) => (
                        <div key={c.label}>
                            <div className="briefing-doc__card-heading" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {c.label}
                                {c.reasoning && (
                                    <i
                                        className="pi pi-info-circle"
                                        style={{
                                            color: 'var(--text-muted)',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem'
                                        }}
                                        onClick={() => onReasoningClick(c.reasoning!)}
                                        title="View reasoning"
                                    />
                                )}
                            </div>
                            <div className="briefing-doc__card-subheading">{c.subLabel}</div>
                            <div className={`briefing-doc__card-value${c.isBatna ? " briefing-doc__card-value-batna" : ""}`}>
                                {c.value ?? ""}
                            </div>
                            {c.supplierBid && (
                                <div className="briefing-doc__card-supplier">
                                    <span className="briefing-doc__card-supplier-label">Supplier Bid</span>
                                    <span className="briefing-doc__card-supplier-value">{c.supplierBid}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const DisplayGroupAccordion = ({ group, onVariantInfoClick }: DisplayGroupAccordionProps) => {
    const [open, setOpen] = useState(false);

    return (
        <div className={`briefing-doc__target-accordion ${open ? "is-open" : ""}`}>
            <button
                type="button"
                className="briefing-doc__target-toggle"
                onClick={() => setOpen(!open)}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {group.displayDescription || group.display}
                    <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        opacity: 0.8,
                        marginLeft: '4px'
                    }}>
                        ({group.variants.length} variant{group.variants.length !== 1 ? 's' : ''})
                    </span>
                </span>
                <i className={`pi ${open ? "pi-chevron-up" : "pi-chevron-down"}`} />
            </button>
            {open && (
                <div className="briefing-doc__target-cards briefing-doc__display-content">
                    {/* Display-level summary: Mix Price and Total Case Size */}
                    <div className="briefing-doc__display-mix-price">
                        <div>
                            <div className="briefing-doc__card-heading">Mix Price</div>
                            <div className="briefing-doc__card-subheading">(Weighted Average)</div>
                            <div className="briefing-doc__card-value">
                                {formatCurrency(group.mixPrice, group.mixPriceCurrency) || '-'}
                            </div>
                        </div>
                        <div>
                            <div className="briefing-doc__card-heading">Total Case Size</div>
                            <div className="briefing-doc__card-subheading">(Sum of Case Quantity)</div>
                            <div className="briefing-doc__card-value">
                                {formatNumber(group.totalCaseQuantity)}
                            </div>
                        </div>
                    </div>

                    {/* Nested variant accordions */}
                    <div className="briefing-doc__nested-variants">
                        {group.variants.length === 0 ? (
                            <div className="briefing-doc__muted">No variants available for this display.</div>
                        ) : (
                            group.variants.map((variant) => (
                                <VariantAccordion
                                    key={variant.tenderSourcingProductId}
                                    variant={variant}
                                    onInfoClick={() => {
                                        if (variant.target?.reasoning?.targetPriceReasoning) {
                                            onVariantInfoClick(variant.target.reasoning.targetPriceReasoning);
                                        }
                                    }}
                                    onReasoningClick={(reasoning) => onVariantInfoClick(reasoning)}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DisplayGroupAccordion;
